import Alpine from 'alpinejs'
import focus from '@alpinejs/focus'
import collapse from '@alpinejs/collapse'

// Import styles
import './style.css'
import './scss/main.scss'

// Register Alpine.js plugins
Alpine.plugin(focus)
Alpine.plugin(collapse)

// Alpine.js components
Alpine.data('mobileMenu', () => ({
  open: false,
  toggleButton: null,
  panel: null,
  lastFocusedElement: null,
  focusableElements: [],
  touchStartX: 0,
  touchStartY: 0,
  touchCurrentX: 0,
  isDragging: false,

  toggle() {
    this.open = !this.open
  },

  close() {
    if (!this.open) return

    this.open = false

    // Return focus to toggle button
    this.$nextTick(() => {
      if (this.toggleButton) {
        this.toggleButton.focus()
      }
    })
  },

  init() {
    // Store references
    this.toggleButton = this.$el.querySelector('.mobile-nav__toggle')
    this.panel = this.$el.querySelector('.mobile-nav__panel')

    // Watch for open state changes
    this.$watch('open', (value) => {
      if (value) {
        this.onOpen()
      } else {
        this.onClose()
      }
    })

    // Handle orientation changes
    window.addEventListener('orientationchange', () => {
      if (this.open) {
        // Slight delay to allow for orientation change to complete
        setTimeout(() => {
          this.updateFocusableElements()
        }, 100)
      }
    })

    // Handle browser back button
    window.addEventListener('popstate', () => {
      if (this.open) {
        this.close()
      }
    })

    // Setup touch gestures for swipe-to-close
    if (this.panel) {
      this.panel.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true })
      this.panel.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
      this.panel.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true })
    }
  },

  onOpen() {
    // Store the last focused element
    this.lastFocusedElement = document.activeElement

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    // Update ARIA attributes
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'true')
    }

    // Setup focus trap after transition
    this.$nextTick(() => {
      setTimeout(() => {
        this.updateFocusableElements()
        this.trapFocus()

        // Focus first focusable element or close button
        const firstFocusable = this.panel?.querySelector('.mobile-nav__close')
        if (firstFocusable) {
          firstFocusable.focus()
        } else if (this.focusableElements.length > 0) {
          this.focusableElements[0].focus()
        }

        // Announce to screen readers
        this.announceStateChange('Mobile menu opened')
      }, 300) // Wait for transition
    })

    // Add event listeners
    document.addEventListener('keydown', this.handleKeydown.bind(this))
  },

  onClose() {
    // Restore body scroll
    document.body.style.overflow = ''

    // Update ARIA attributes
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false')
    }

    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeydown.bind(this))

    // Announce to screen readers
    this.announceStateChange('Mobile menu closed')
  },

  updateFocusableElements() {
    if (!this.panel) return

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ]

    this.focusableElements = Array.from(
      this.panel.querySelectorAll(focusableSelectors.join(','))
    ).filter(el => {
      // Filter out hidden elements
      return el.offsetParent !== null
    })
  },

  trapFocus() {
    if (!this.panel || this.focusableElements.length === 0) return

    const firstFocusable = this.focusableElements[0]
    const lastFocusable = this.focusableElements[this.focusableElements.length - 1]

    this.panel.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return

      // Shift + Tab
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault()
          lastFocusable.focus()
        }
      }
      // Tab
      else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault()
          firstFocusable.focus()
        }
      }
    })
  },

  handleKeydown(event) {
    if (!this.open) return

    switch(event.key) {
      case 'Escape':
        event.preventDefault()
        this.close()
        break

      case 'Tab':
        // Focus trap is handled in trapFocus()
        break
    }
  },

  // Touch gesture handling for swipe-to-close
  handleTouchStart(event) {
    if (!this.open) return

    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    this.touchCurrentX = this.touchStartX
    this.isDragging = false
  },

  handleTouchMove(event) {
    if (!this.open) return

    this.touchCurrentX = event.touches[0].clientX
    const touchCurrentY = event.touches[0].clientY

    const deltaX = this.touchCurrentX - this.touchStartX
    const deltaY = touchCurrentY - this.touchStartY

    // Only consider horizontal swipes (more horizontal than vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      this.isDragging = true

      // Only allow swiping right (to close)
      if (deltaX > 0) {
        // Prevent scroll during swipe
        event.preventDefault()

        // Apply visual feedback with transform
        const translateX = Math.min(deltaX, window.innerWidth)
        if (this.panel) {
          this.panel.style.transform = `translateX(${translateX}px)`
          this.panel.style.transition = 'none'
        }
      }
    }
  },

  handleTouchEnd(event) {
    if (!this.open || !this.isDragging) {
      // Reset panel transform
      if (this.panel) {
        this.panel.style.transform = ''
        this.panel.style.transition = ''
      }
      return
    }

    const deltaX = this.touchCurrentX - this.touchStartX
    const threshold = window.innerWidth * 0.3 // 30% of screen width

    // Reset panel transform
    if (this.panel) {
      this.panel.style.transform = ''
      this.panel.style.transition = ''
    }

    // Close menu if swipe distance exceeds threshold
    if (deltaX > threshold) {
      this.close()
    }

    this.isDragging = false
  },

  // Close menu on internal link navigation
  handleLinkClick(event) {
    const link = event.target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    // Check if it's an internal link (starts with / but not //)
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      // Close menu after a slight delay to allow navigation to feel responsive
      setTimeout(() => {
        this.close()
      }, 150)
    }
  },

  announceStateChange(message) {
    // Create or update live region for screen reader announcements
    let liveRegion = document.getElementById('mobile-menu-live-region')

    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'mobile-menu-live-region'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      liveRegion.className = 'sr-only'
      document.body.appendChild(liveRegion)
    }

    // Clear and set message
    liveRegion.textContent = ''
    setTimeout(() => {
      liveRegion.textContent = message
    }, 100)
  }
}))

Alpine.data('dropdown', () => ({
  open: false,
  toggle() {
    this.open = !this.open
  },
  close() {
    this.open = false
  },
  init() {
    // Close dropdown when clicking outside
    this.$watch('open', (value) => {
      if (value) {
        document.addEventListener('click', this.handleClickOutside)
        document.addEventListener('keydown', this.handleEscape)
      } else {
        document.removeEventListener('click', this.handleClickOutside)
        document.removeEventListener('keydown', this.handleEscape)
      }
    })
  },
  handleClickOutside(event) {
    if (!this.$el.contains(event.target)) {
      this.close()
    }
  },
  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }
}))

// Navigation dropdown component for mobile
Alpine.data('mobileDropdown', () => ({
  open: false,
  toggleButton: null,
  submenu: null,
  parentItem: null,

  toggle() {
    this.open = !this.open

    // Update ARIA attributes
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', this.open.toString())
    }

    // Announce state change to screen readers
    if (this.open) {
      this.announceStateChange('Submenu expanded')
    } else {
      this.announceStateChange('Submenu collapsed')
    }
  },

  close() {
    this.open = false

    // Update ARIA attributes
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false')
    }
  },

  init() {
    // Store references
    this.toggleButton = this.$el.querySelector('.mobile-nav__dropdown-toggle')
    this.submenu = this.$el.querySelector('.mobile-nav__dropdown')
    this.parentItem = this.$el

    // Set up keyboard navigation
    if (this.toggleButton) {
      this.toggleButton.addEventListener('keydown', this.handleKeydown.bind(this))
    }

    // Handle submenu link clicks to close entire mobile menu
    if (this.submenu) {
      this.submenu.addEventListener('click', (event) => {
        const link = event.target.closest('a')
        if (link) {
          // Find parent mobile menu component and close it
          const mobileMenuEl = this.$el.closest('[x-data*="mobileMenu"]')
          if (mobileMenuEl && mobileMenuEl.__x) {
            const mobileMenu = mobileMenuEl.__x.$data
            if (mobileMenu && typeof mobileMenu.handleLinkClick === 'function') {
              mobileMenu.handleLinkClick(event)
            }
          }
        }
      })
    }

    // Initialize ARIA attributes
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false')

      // Set aria-controls if submenu has an ID
      if (this.submenu && !this.submenu.id) {
        const uniqueId = `mobile-dropdown-${Math.random().toString(36).substr(2, 9)}`
        this.submenu.id = uniqueId
        this.toggleButton.setAttribute('aria-controls', uniqueId)
      } else if (this.submenu && this.submenu.id) {
        this.toggleButton.setAttribute('aria-controls', this.submenu.id)
      }
    }
  },

  handleKeydown(event) {
    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!this.open) {
          this.toggle()
        }
        // Focus first link in submenu
        this.$nextTick(() => {
          const firstLink = this.submenu?.querySelector('a')
          if (firstLink) {
            firstLink.focus()
          }
        })
        break

      case 'ArrowUp':
        event.preventDefault()
        if (this.open) {
          this.close()
        }
        break

      case 'Enter':
      case ' ':
        event.preventDefault()
        this.toggle()
        break

      case 'Escape':
        if (this.open) {
          event.preventDefault()
          this.close()
          this.toggleButton?.focus()
        }
        break
    }
  },

  announceStateChange(message) {
    // Use the same live region as mobile menu or create a new one
    let liveRegion = document.getElementById('mobile-menu-live-region')

    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'mobile-menu-live-region'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      liveRegion.className = 'sr-only'
      document.body.appendChild(liveRegion)
    }

    // Clear and set message
    liveRegion.textContent = ''
    setTimeout(() => {
      liveRegion.textContent = message
    }, 100)
  }
}))

// Newsletter form component
Alpine.data('newsletterForm', () => ({
  email: '',
  loading: false,
  success: false,
  error: '',
  
  async submit() {
    if (!this.email || !this.isValidEmail(this.email)) {
      this.error = 'Please enter a valid email address'
      return
    }
    
    this.loading = true
    this.error = ''
    
    try {
      // Replace with actual newsletter signup endpoint
      const response = await fetch('/newsletter/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: this.email })
      })
      
      if (response.ok) {
        this.success = true
        this.email = ''
      } else {
        throw new Error('Signup failed')
      }
    } catch (error) {
      this.error = 'Sorry, there was an error. Please try again.'
    } finally {
      this.loading = false
    }
  },
  
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  },
  
  reset() {
    this.success = false
    this.error = ''
  }
}))

Alpine.data('accordion', () => ({
  openItems: new Set(),
  allowMultiple: true,

  toggle(itemId) {
    if (this.openItems.has(itemId)) {
      this.openItems.delete(itemId)
    } else {
      if (!this.allowMultiple) {
        // Close all other items if multiple not allowed
        this.openItems.clear()
      }
      this.openItems.add(itemId)
    }
  },

  isOpen(itemId) {
    return this.openItems.has(itemId)
  }
}))

// Testimonial carousel component
Alpine.data('testimonialCarousel', () => ({
  currentIndex: 0,
  totalCount: 0,
  testimonialElements: null,

  init() {
    // Store reference to testimonial elements
    this.testimonialElements = this.$el.querySelectorAll('.testimonial')
    this.totalCount = this.testimonialElements.length
    this.showTestimonial(0)
  },

  showTestimonial(index) {
    // Use stored element reference instead of querying again
    this.testimonialElements.forEach((testimonial, i) => {
      testimonial.style.display = i === index ? 'block' : 'none'
    })
    this.currentIndex = index
  },

  next() {
    const nextIndex = (this.currentIndex + 1) % this.totalCount
    this.showTestimonial(nextIndex)
  },

  prev() {
    const prevIndex = this.currentIndex === 0 ? this.totalCount - 1 : this.currentIndex - 1
    this.showTestimonial(prevIndex)
  }
}))

// Stories carousel component - horizontal scrolling
Alpine.data('storiesCarousel', () => ({
  scrollContainer: null,
  canScrollPrev: false,
  canScrollNext: false,
  prevButton: null,
  nextButton: null,
  totalStories: 0,
  storiesPerPage: 1,
  currentPage: 0,

  init() {
    // Find the horizontal container within the stories grid
    this.scrollContainer = this.$refs.storiesContainer.querySelector('.stories-horizontal__container')
    this.prevButton = this.$el.querySelector('.stories-pager-prev')
    this.nextButton = this.$el.querySelector('.stories-pager-next')

    if (this.scrollContainer) {
      // Count total stories
      const storyItems = this.scrollContainer.querySelectorAll('.stories-horizontal__item')
      this.totalStories = storyItems.length

      // Wait for content to load
      this.$nextTick(() => {
        this.calculateStoriesPerPage()
        this.checkScrollBounds()
        this.updateButtonStates()
        this.hideButtonsIfNotNeeded()
      })

      this.scrollContainer.addEventListener('scroll', this.handleScroll.bind(this))
      window.addEventListener('resize', this.handleResize.bind(this))
    } else {
      // Fallback for non-horizontal layout: hide buttons if all stories are visible
      const allStoryCards = this.$refs.storiesContainer.querySelectorAll('.story-card')
      this.totalStories = allStoryCards.length
      this.$nextTick(() => {
        this.hideButtonsIfNotNeeded()
      })
    }
  },

  calculateStoriesPerPage() {
    if (!this.scrollContainer) return

    // Get the visible container width (stories-grid or stories-component)
    const visibleContainer = this.$refs.storiesContainer
    const visibleWidth = visibleContainer.clientWidth
    const storyWidth = 403 + 32 // Story width + gap from SCSS
    this.storiesPerPage = Math.floor(visibleWidth / storyWidth)

    // Ensure at least 1 story per page
    if (this.storiesPerPage < 1) this.storiesPerPage = 1
  },

  hideButtonsIfNotNeeded() {
    if (!this.scrollContainer) return

    // Check if content overflows the visible container
    const visibleContainer = this.$refs.storiesContainer
    const contentWidth = this.scrollContainer.scrollWidth
    const visibleWidth = visibleContainer.clientWidth

    // If content fits within visible area, hide buttons
    if (contentWidth <= visibleWidth) {
      if (this.prevButton) this.prevButton.style.display = 'none'
      if (this.nextButton) this.nextButton.style.display = 'none'
    } else {
      if (this.prevButton) this.prevButton.style.display = 'block'
      if (this.nextButton) this.nextButton.style.display = 'block'
    }
  },

  handleResize() {
    this.calculateStoriesPerPage()
    this.checkScrollBounds()
    this.updateButtonStates()
    this.hideButtonsIfNotNeeded()
  },

  checkScrollBounds() {
    if (!this.scrollContainer) return

    const scrollLeft = this.scrollContainer.scrollLeft

    // Check overflow against the visible container, not the scroll container itself
    const visibleContainer = this.$refs.storiesContainer
    const contentWidth = this.scrollContainer.scrollWidth
    const visibleWidth = visibleContainer.clientWidth
    const maxScroll = Math.max(0, contentWidth - visibleWidth)

    // If there's no scrollable content, disable both buttons
    if (maxScroll <= 0) {
      this.canScrollPrev = false
      this.canScrollNext = false
    } else {
      this.canScrollPrev = scrollLeft > 5 // Small threshold for precision
      this.canScrollNext = scrollLeft < maxScroll - 5
    }

    this.updateButtonStates()
  },

  updateButtonStates() {
    if (this.prevButton) {
      this.prevButton.disabled = !this.canScrollPrev
    }
    if (this.nextButton) {
      this.nextButton.disabled = !this.canScrollNext
    }
  },

  handleScroll() {
    // Debounce scroll events
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(() => {
      this.checkScrollBounds()
    }, 10)
  },

  scrollPrev() {
    if (!this.scrollContainer || !this.canScrollPrev) return

    const storyWidth = 403 + 32 // Story width + gap
    const currentScroll = this.scrollContainer.scrollLeft
    const newScroll = Math.max(0, currentScroll - storyWidth)

    this.scrollContainer.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    })
  },

  scrollNext() {
    if (!this.scrollContainer || !this.canScrollNext) return

    const storyWidth = 403 + 32 // Story width + gap
    const currentScroll = this.scrollContainer.scrollLeft

    // Calculate max scroll based on visible container width
    const visibleContainer = this.$refs.storiesContainer
    const contentWidth = this.scrollContainer.scrollWidth
    const visibleWidth = visibleContainer.clientWidth
    const maxScroll = Math.max(0, contentWidth - visibleWidth)

    const newScroll = Math.min(maxScroll, currentScroll + storyWidth)

    this.scrollContainer.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    })
  }
}))

// Search toggle component — delegates to Alpine.store('search')
Alpine.data('searchToggle', () => ({
  get open() {
    return Alpine.store('search').open
  },
  set open(value) {
    Alpine.store('search').open = value
  },
  toggle() {
    Alpine.store('search').toggle()
  },
  close() {
    Alpine.store('search').close()
  },
  init() {
    // Focus search input when panel opens
    this.$watch(() => Alpine.store('search').open, (value) => {
      if (value) {
        this.$nextTick(() => {
          const searchInput = this.$el.querySelector('.form-search')
          if (searchInput) {
            searchInput.focus()
          }
        })
      }
    })

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && Alpine.store('search').open) {
        Alpine.store('search').close()
      }
    })

    // Close on click outside (check both panel and toggle button)
    document.addEventListener('click', (e) => {
      if (!Alpine.store('search').open) return
      const panel = document.querySelector('.search-panel')
      const toggleBtn = document.querySelector('.utility-navigation__search .search-toggle-button')
      if (panel && !panel.contains(e.target) && (!toggleBtn || !toggleBtn.contains(e.target))) {
        Alpine.store('search').close()
      }
    })
  }
}))

// Gallery scroll component for horizontal scrolling with arrows
Alpine.data('galleryScroll', () => ({
  scrollPosition: 0,
  canScrollPrev: false,
  canScrollNext: true,
  imageWidth: 320, // Minimum width
  imageCount: 0,
  isScrolling: false,
  
  init() {
    // Get image count from data attribute
    const imagesContainer = this.$el.querySelector('.gallery-images-field__items')
    if (imagesContainer) {
      this.imageCount = parseInt(imagesContainer.dataset.imageCount) || 0
      
      // Calculate dynamic image width based on viewport and image count
      this.calculateImageWidth()
    }
    
    // Check scroll bounds on init
    this.$nextTick(() => {
      this.checkScrollBounds()
    })
    
    // Check on window resize
    window.addEventListener('resize', () => {
      this.calculateImageWidth()
      this.checkScrollBounds()
    })
  },
  
  calculateImageWidth() {
    const viewportWidth = window.innerWidth
    const divisor = this.imageCount === 4 ? 4 : 8
    this.imageWidth = Math.max(320, viewportWidth / divisor)
  },
  
  checkScrollBounds() {
    if (!this.$refs.scrollContainer) return
    
    const container = this.$refs.scrollContainer
    const scrollLeft = container.scrollLeft
    const maxScroll = container.scrollWidth - container.clientWidth
    
    // Check if we can scroll previous (not at start)
    this.canScrollPrev = scrollLeft > 0
    
    // Check if we can scroll next (not at end)
    this.canScrollNext = scrollLeft < maxScroll - 1
  },
  
  handleScroll() {
    if (!this.$refs.scrollContainer || this.isScrolling) return
    
    const container = this.$refs.scrollContainer
    const scrollLeft = container.scrollLeft
    
    // Update scroll position
    this.scrollPosition = scrollLeft
    
    // Check scroll bounds to enable/disable buttons
    this.checkScrollBounds()
  },
  
  scrollPrev() {
    if (!this.$refs.scrollContainer || !this.canScrollPrev) return
    
    const container = this.$refs.scrollContainer
    const scrollAmount = this.imageWidth * 3
    const currentScroll = container.scrollLeft
    
    // Scroll to previous position, but not past 0
    const targetScroll = Math.max(0, currentScroll - scrollAmount)
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    })
  },
  
  scrollNext() {
    if (!this.$refs.scrollContainer || !this.canScrollNext) return
    
    const container = this.$refs.scrollContainer
    const scrollAmount = this.imageWidth * 3
    const currentScroll = container.scrollLeft
    const maxScroll = container.scrollWidth - container.clientWidth
    
    // Scroll to next position, but not past max
    const targetScroll = Math.min(maxScroll, currentScroll + scrollAmount)
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    })
  },
  
  // Touch/swipe support for mobile
  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX
  },
  
  handleTouchEnd(e) {
    if (!this.touchStartX) return
    
    const touchEndX = e.changedTouches[0].clientX
    const diff = this.touchStartX - touchEndX
    
    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        this.scrollNext()
      } else {
        this.scrollPrev()
      }
    }
    
    this.touchStartX = null
  }
}))

// Logo carousel component for auto-scrolling
Alpine.data('logoCarousel', () => ({
  scrollSpeed: 30, // Duration in seconds for full scroll
  isPaused: false,
  styleSheet: null, // Store reference to dynamically created stylesheet
  resizeTimeout: null, // Store timeout for debouncing
  
  init() {
    // Initial setup
    this.checkAndSetupScroll()
    
    // Add resize event listener with debouncing
    this.handleResize = this.debounce(() => {
      this.checkAndSetupScroll()
    }, 250) // 250ms debounce delay
    
    window.addEventListener('resize', this.handleResize)
    
    // Pause on hover
    this.$el.addEventListener('mouseenter', () => {
      this.pause()
    })
    
    this.$el.addEventListener('mouseleave', () => {
      this.play()
    })
    
    // Cleanup on component destroy
    this.$cleanup = () => {
      window.removeEventListener('resize', this.handleResize)
      this.cleanupAnimation()
    }
  },
  
  debounce(func, wait) {
    return () => {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => func.apply(this), wait)
    }
  },
  
  checkAndSetupScroll() {
    // Get elements
    const container = this.$el.querySelector('.logos-overflow-wrapper')
    const scrollContainer = this.$el.querySelector('.logos-scroll-container')
    const firstWrapper = this.$el.querySelector('.logos-wrapper')
    const duplicate = this.$el.querySelector('.logos-wrapper-duplicate')
    
    if (container && scrollContainer && firstWrapper) {
      // Clean up existing animation before recalculating
      this.cleanupAnimation()
      
      // Force layout recalculation
      void container.offsetWidth
      
      // Calculate exact width of one logo set
      const logoSetWidth = firstWrapper.offsetWidth
      const containerWidth = container.offsetWidth
      
      // Check if scrolling is needed
      if (logoSetWidth > containerWidth) {
        // Content overflows, enable scrolling
        // Show duplicate set for scrolling
        if (duplicate) {
          duplicate.style.display = 'flex'
        }
        
        // Add scrolling-active class for gradient masks
        container.classList.add('scrolling-active')
        // Add scrolling class to scroll container for layout
        scrollContainer.classList.add('scrolling')
        
        // Set custom property for exact translation distance
        // We need to move by exactly one logo set width + the gap (50px)
        this.$el.style.setProperty('--logo-set-width', `${logoSetWidth + 50}px`)
        
        // Create custom keyframes for exact translation
        this.styleSheet = document.createElement('style')
        this.styleSheet.textContent = `
          @keyframes scroll-logos-precise {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-${logoSetWidth + 50}px);
            }
          }
        `
        document.head.appendChild(this.styleSheet)
        
        // Apply the precise animation
        scrollContainer.style.animation = `scroll-logos-precise ${this.scrollSpeed}s linear infinite`
        scrollContainer.style.setProperty('--scroll-duration', `${this.scrollSpeed}s`)
        
        // Restore animation play state if it was paused
        if (this.isPaused) {
          scrollContainer.style.animationPlayState = 'paused'
        }
      } else {
        // No overflow, disable scrolling
        container.classList.remove('scrolling-active')
        scrollContainer.classList.remove('scrolling')
        scrollContainer.style.animation = 'none'
        // Hide duplicate set when not scrolling
        if (duplicate) {
          duplicate.style.display = 'none'
        }
      }
    }
  },
  
  cleanupAnimation() {
    // Remove dynamically created stylesheet
    if (this.styleSheet && this.styleSheet.parentNode) {
      this.styleSheet.parentNode.removeChild(this.styleSheet)
      this.styleSheet = null
    }
    
    // Clear animation from scroll container
    const scrollContainer = this.$el.querySelector('.logos-scroll-container')
    if (scrollContainer) {
      scrollContainer.style.animation = ''
      scrollContainer.style.animationPlayState = ''
    }
  },
  
  pause() {
    this.isPaused = true
    const scrollContainer = this.$el.querySelector('.logos-scroll-container')
    if (scrollContainer) {
      scrollContainer.style.animationPlayState = 'paused'
    }
  },
  
  play() {
    this.isPaused = false
    const scrollContainer = this.$el.querySelector('.logos-scroll-container')
    if (scrollContainer) {
      scrollContainer.style.animationPlayState = 'running'
    }
  },
  
  destroy() {
    // Cleanup when component is destroyed
    if (this.$cleanup) {
      this.$cleanup()
    }
  }
}))

// Video Player component for YouTube integration
Alpine.data('videoPlayer', () => ({
  isPlaying: false,
  youtubeId: '',

  init() {
    // Get YouTube ID from data attribute
    this.youtubeId = this.$el.dataset.youtubeId || '0utyOa3bRJo'

    // Add fallback event listener for play button
    const playButton = this.$el.querySelector('.post-page__video-play')
    if (playButton) {
      playButton.addEventListener('click', (e) => {
        e.preventDefault()
        this.playVideo()
      })
    }
  },

  playVideo() {
    if (!this.isPlaying) {
      this.isPlaying = true

      // Create and insert YouTube iframe
      const wrapper = this.$el.querySelector('.post-page__video-wrapper')
      if (wrapper) {
        const iframe = document.createElement('iframe')
        iframe.width = '840'
        iframe.height = '473'
        iframe.src = `https://www.youtube.com/embed/${this.youtubeId}?autoplay=1&rel=0&modestbranding=1`
        iframe.setAttribute('frameborder', '0')
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        iframe.allowFullscreen = true
        iframe.classList.add('post-page__video-iframe')

        // Replace content with iframe
        wrapper.innerHTML = ''
        wrapper.appendChild(iframe)

        // Hide controls since video is now playing
        const controls = this.$el.querySelector('.post-page__video-controls')
        if (controls) {
          controls.style.display = 'none'
        }
      }
    }
  },

  stopVideo() {
    if (this.isPlaying) {
      this.isPlaying = false
      // Reload the page or restore thumbnail if needed
      location.reload()
    }
  }
}))

// Toggle Group component for interactive toggle navigation
Alpine.data('toggleGroup', () => ({
  activeToggle: 0,
  totalToggles: 0,
  loading: false,
  error: false,

  init() {
    // Get total toggle count from data attribute
    const toggleCount = this.$el.dataset.toggleCount
    this.totalToggles = parseInt(toggleCount) || 0

    // Ensure we have at least one toggle to show
    if (this.totalToggles > 0) {
      this.showToggle(0)
    }

    // Set up keyboard navigation for the component
    this.setupKeyboardNavigation()
  },

  switchToggle(index) {
    if (index >= 0 && index < this.totalToggles && index !== this.activeToggle) {
      this.showToggle(index)
    }
  },

  showToggle(index) {
    if (index >= 0 && index < this.totalToggles) {
      this.activeToggle = index

      // Update ARIA attributes for accessibility
      this.updateAriaAttributes()

      // Announce change to screen readers
      this.announceToggleChange(index)
    }
  },

  nextToggle() {
    const nextIndex = (this.activeToggle + 1) % this.totalToggles
    this.switchToggle(nextIndex)
  },

  previousToggle() {
    const prevIndex = this.activeToggle === 0 ? this.totalToggles - 1 : this.activeToggle - 1
    this.switchToggle(prevIndex)
  },

  updateAriaAttributes() {
    // Update tab buttons
    const tabButtons = this.$el.querySelectorAll('[role="tab"]')
    tabButtons.forEach((button, index) => {
      button.setAttribute('aria-selected', index === this.activeToggle ? 'true' : 'false')
      button.setAttribute('tabindex', index === this.activeToggle ? '0' : '-1')
    })

    // Update tab panels
    const tabPanels = this.$el.querySelectorAll('[role="tabpanel"]')
    tabPanels.forEach((panel, index) => {
      panel.setAttribute('aria-hidden', index !== this.activeToggle ? 'true' : 'false')
    })
  },

  setupKeyboardNavigation() {
    // Ensure proper tab order for keyboard navigation
    this.$nextTick(() => {
      this.updateAriaAttributes()
    })
  },

  announceToggleChange(index) {
    // Create announcement for screen readers
    const announcement = `Toggle ${index + 1} of ${this.totalToggles} selected`

    // Find or create live region for announcements
    let liveRegion = document.getElementById('toggle-live-region')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'toggle-live-region'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      liveRegion.className = 'sr-only'
      document.body.appendChild(liveRegion)
    }

    // Announce the change
    liveRegion.textContent = announcement
  },

  // Error handling methods
  setLoading(isLoading) {
    this.loading = isLoading
    this.$el.setAttribute('data-loading', isLoading.toString())
  },

  setError(hasError) {
    this.error = hasError
    this.$el.setAttribute('data-error', hasError.toString())
  },

  // Public API methods for external control
  goToToggle(index) {
    this.switchToggle(index)
  },

  getCurrentToggle() {
    return this.activeToggle
  },

  getTotalToggles() {
    return this.totalToggles
  }
}))

// Social Share component for sharing pages to social platforms
Alpine.data('socialShare', () => ({
  copied: false,
  supportsWebShare: false,
  pageUrl: '',
  pageTitle: '',

  init() {
    this.supportsWebShare = 'share' in navigator
    this.pageUrl = window.location.href
    this.pageTitle = document.title
  },

  async nativeShare() {
    if (this.supportsWebShare) {
      try {
        await navigator.share({
          title: this.pageTitle,
          url: this.pageUrl
        })
      } catch (err) {
        // User cancelled or share failed - only log actual errors
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err)
        }
      }
    }
  },

  async copyToClipboard() {
    try {
      await navigator.clipboard.writeText(this.pageUrl)
      this.copied = true
      this.announceToScreenReader('Link copied to clipboard')
      setTimeout(() => {
        this.copied = false
      }, 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  },

  shareToFacebook() {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.pageUrl)}`
    this.openShareWindow(url)
  },

  shareToTwitter() {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.pageUrl)}&text=${encodeURIComponent(this.pageTitle)}`
    this.openShareWindow(url)
  },

  shareToLinkedIn() {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.pageUrl)}`
    this.openShareWindow(url)
  },

  shareViaEmail() {
    window.location.href = `mailto:?subject=${encodeURIComponent(this.pageTitle)}&body=${encodeURIComponent(this.pageUrl)}`
  },

  openShareWindow(url) {
    window.open(url, 'share', 'width=600,height=400,menubar=no,toolbar=no')
  },

  announceToScreenReader(message) {
    let region = document.getElementById('social-share-live-region')

    if (!region) {
      region = document.createElement('div')
      region.id = 'social-share-live-region'
      region.setAttribute('aria-live', 'polite')
      region.setAttribute('aria-atomic', 'true')
      region.className = 'sr-only'
      document.body.appendChild(region)
    }

    // Clear and set message with slight delay for screen readers
    region.textContent = ''
    setTimeout(() => {
      region.textContent = message
    }, 100)
  }
}))

// Alpine.js store for search panel state (shared between toggle button and panel)
Alpine.store('search', {
  open: false,
  toggle() {
    this.open = !this.open
  },
  close() {
    this.open = false
  }
})

// Make Alpine.js available globally for debugging
window.Alpine = Alpine

// Initialize Alpine.js
Alpine.start()

// Debug Alpine.js initialization
console.log('Alpine.js initialized:', !!window.Alpine)
console.log('Alpine.js version:', Alpine.version)

// Modern JavaScript utilities
class ThemeUtils {
  static initializeAccessibility() {
    // Add skip link functionality
    const skipLink = document.querySelector('.skip-link')
    if (skipLink) {
      skipLink.addEventListener('click', (e) => {
        e.preventDefault()
        const target = document.querySelector('#main-content')
        if (target) {
          target.focus()
          target.scrollIntoView({ behavior: 'smooth' })
        }
      })
    }
    
    // Improve focus management for modals
    this.setupModalFocusTrap()
    
    // Add reduced motion support
    this.respectReducedMotion()
  }
  
  static setupModalFocusTrap() {
    const modals = document.querySelectorAll('[data-modal]')
    modals.forEach(modal => {
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          modal.setAttribute('aria-hidden', 'true')
          modal.classList.add('hidden')
        }
      })
    })
  }
  
  static respectReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    
    if (prefersReducedMotion.matches) {
      document.documentElement.style.setProperty('--animation-duration', '0.01ms')
      document.documentElement.style.setProperty('--transition-duration', '0.01ms')
    }
  }
  
  static initializeLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target
            img.src = img.dataset.src
            img.classList.remove('lazy')
            observer.unobserve(img)
          }
        })
      })
      
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img)
      })
    }
  }
}

// Search toggle functionality — vanilla JS fallback for the search panel
class SearchToggle {
  constructor() {
    this.searchPanel = document.querySelector('.search-panel')
    this.searchToggleButton = document.querySelector('.utility-navigation__search .search-toggle-button')
    this.closeButton = this.searchPanel?.querySelector('.search-panel__close')
    this.searchInput = this.searchPanel?.querySelector('.form-search, .form-text')
    this.isOpen = false

    this.init()
  }

  init() {
    if (!this.searchPanel || !this.searchToggleButton) {
      return
    }

    // Toggle button click (fallback if Alpine doesn't load)
    this.searchToggleButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.toggle()
    })

    // Close button click
    this.closeButton?.addEventListener('click', () => {
      this.close()
    })

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.isOpen) return
      if (!this.searchPanel.contains(e.target) && !this.searchToggleButton.contains(e.target)) {
        this.close()
      }
    })

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close()
      }
    })
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.isOpen = true
    this.searchPanel.style.display = 'block'
    this.searchToggleButton.setAttribute('aria-expanded', 'true')
    setTimeout(() => {
      this.searchInput?.focus()
    }, 100)
  }

  close() {
    this.isOpen = false
    this.searchPanel.style.display = 'none'
    this.searchToggleButton.setAttribute('aria-expanded', 'false')
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  ThemeUtils.initializeAccessibility()
  ThemeUtils.initializeLazyLoading()
  
  // Initialize search toggle
  new SearchToggle()
  
  console.log('Kessler 2025 theme initialized')
})